import { useQueries } from '@tanstack/react-query';

import type { AdminEstablishmentDto } from '@/api/supplier-client';
import { useSupplierClient } from '@/api/supplier-client-context';
import { supplierDetailKey } from '@/api/use-suppliers';

// =============================================================================
// use-active-contacts-resolver.ts — Contacts ACTIFS par SIRET (composition front, DEC-48).
// =============================================================================
// FIX-FOU-2 / DEC-46/DEC-48 — la signature CGU passe du grain SIRET au grain CONTACT (sémantique
// ALL) : un SIRET est listé non-signataire dès qu'≥1 de ses contacts ACTIFS n'a pas signé la version
// courante. Le calcul croise DEUX sources qui ne se parlent JAMAIS côté serveur (invariant n°2) :
//   - les contactIds ayant signé, par SIRET (legal : `signed-contacts`) ;
//   - les contactIds ACTIFS, par SIRET (supplier-reference : `getEstablishment`).
//
// Le BC legal ne connaît PAS l'état Actif des contacts (il vit dans supplier-reference). L'ensemble
// des contacts Actifs est donc résolu ICI, par fan-out `getEstablishment(siret)` sur les SIRET de la
// liste des fournisseurs, filtré sur `state ∈ {Actif, ResignatureRequise}`. MÊME `queryKey` que la fiche A-04 / le
// resolver d'identité (`supplierDetailKey`) : cache TanStack PARTAGÉ, aucune requête redondante.

/** Résultat du resolver : map siret → contactIds ACTIFS + états agrégés du fan-out. */
export interface ActiveContactsResolution {
  /** siret → ensemble des contactIds ACTIFS. Absent = établissement non résolu (encore en cours / erreur). */
  readonly activeContactIdsBySiret: ReadonlyMap<string, ReadonlySet<string>>;
  /** Au moins une lecture d'établissement est encore en cours. */
  readonly isPending: boolean;
  /** Au moins une lecture d'établissement a échoué (le calcul ALL serait partiel — l'écran le signale). */
  readonly isError: boolean;
}

/** Déduplique et ordonne (stabilité de `queryKey`) les SIRET non vides. */
function distinctSirets(sirets: readonly string[]): readonly string[] {
  return [...new Set(sirets.filter((s) => s.length > 0))].sort();
}

/**
 * Résout les contactIds ACTIFS par SIRET par fan-out sur les SIRET DISTINCTS. Le tableau d'entrée peut
 * être initialement vide (liste des fournisseurs non encore chargée) : `useQueries([])` ne déclenche
 * alors aucune requête (isPending=false), puis re-render dès que les SIRET arrivent. Chaque
 * établissement expose ses interlocuteurs ; ceux `Actif` OU `ResignatureRequise` comptent pour la règle ALL.
 */
export function useActiveContactsResolver(
  sirets: readonly string[],
): ActiveContactsResolution {
  const client = useSupplierClient();
  const targets = distinctSirets(sirets);

  const results = useQueries({
    queries: targets.map((siret) => ({
      queryKey: supplierDetailKey(siret),
      queryFn: (): Promise<AdminEstablishmentDto> => client.getEstablishment(siret),
    })),
  });

  const activeContactIdsBySiret = new Map<string, ReadonlySet<string>>();
  for (const result of results) {
    const establishment = result.data;
    if (establishment === undefined) {
      continue;
    }
    const activeIds = new Set<string>();
    for (const contact of establishment.interlocuteurs) {
      // FIX-FOU-2b — « tenu d'avoir signé la version courante » = Actif OU ResignatureRequise.
      // La PUBLICATION d'une nouvelle version bascule les signataires Actif→ResignatureRequise
      // (app.module CONTACT_RESIGNATURE_PORT) : ne compter QUE 'Actif' vidait la liste des
      // non-signataires juste après chaque publication (les concernés sont EXACTEMENT les
      // ResignatureRequise). Invite (jamais activé) / Inactif (désactivé) restent exclus.
      if (contact.state === 'Actif' || contact.state === 'ResignatureRequise') {
        activeIds.add(contact.contactId);
      }
    }
    activeContactIdsBySiret.set(establishment.siret, activeIds);
  }

  return {
    activeContactIdsBySiret,
    isPending: results.some((r) => r.isPending),
    isError: results.some((r) => r.isError),
  };
}
